import type React from 'react'
import { cn } from '@/lib/utils'
import { getAuthVariantStyle, type AuthVariant } from '@/components/auth/auth-variant-styles'

export type SocialProvider = 'apple' | 'google'

export interface SocialSignInButton {
  label: string
  loadingLabel: string
  provider: SocialProvider
  onClick: () => void
}

interface SocialSignInControlsProps {
  buttons: SocialSignInButton[]
  isSubmitting: boolean
  isSpecialTheme: boolean
  submittingProvider: SocialProvider | null
  variant: AuthVariant
  separatorLabel: string
}

interface SocialButtonPresentation {
  icon: React.ReactNode
  themeClassName: string
}

interface SocialButtonProps extends SocialSignInButton, SocialButtonPresentation {
  isSubmitting: boolean
  submittingProvider: SocialProvider | null
}

function AppleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 flex-shrink-0" fill="currentColor">
      <path d="M16.36 1.44c0 1.17-.43 2.19-1.28 3.05-.91.92-1.95 1.45-3.11 1.36-.14-1.12.42-2.29 1.24-3.13.87-.89 2.15-1.53 3.15-1.28zM21.25 17.52c-.57 1.31-.84 1.9-1.57 3.06-1.02 1.59-2.46 3.57-4.24 3.59-1.58.02-1.99-1.04-4.14-1.03-2.15.01-2.6 1.06-4.18 1.04-1.78-.02-3.14-1.8-4.16-3.4-2.84-4.43-3.14-9.62-1.39-12.38 1.24-1.96 3.2-3.11 5.04-3.11 1.87 0 3.05 1.03 4.6 1.03 1.5 0 2.42-1.03 4.59-1.03 1.64 0 3.38.89 4.62 2.44-4.06 2.23-3.4 8.03.83 9.79z" />
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 flex-shrink-0">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z" />
    </svg>
  )
}

function getButtonPresentation(
  provider: SocialProvider,
  variant: AuthVariant
): SocialButtonPresentation {
  const { socialButton } = getAuthVariantStyle(variant)
  const presentationByProvider: Record<SocialProvider, SocialButtonPresentation> = {
    apple: { icon: <AppleIcon />, themeClassName: socialButton.apple },
    google: { icon: <GoogleIcon />, themeClassName: socialButton.google }
  }

  return presentationByProvider[provider]
}

function SocialButton({
  label,
  loadingLabel,
  provider,
  themeClassName,
  onClick,
  icon,
  isSubmitting,
  submittingProvider
}: SocialButtonProps) {
  const isLoading = submittingProvider === provider

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isSubmitting}
      className={cn(
        'flex w-full items-center justify-center gap-3 rounded-lg border px-4 py-3 font-bold shadow-sm transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed font-standard sm:px-6',
        themeClassName
      )}
    >
      {!isLoading && icon}
      <span className="whitespace-nowrap">{isLoading ? loadingLabel : label}</span>
    </button>
  )
}

export function SocialSignInControls({
  buttons,
  isSubmitting,
  isSpecialTheme,
  submittingProvider,
  variant,
  separatorLabel
}: SocialSignInControlsProps) {
  if (buttons.length === 0) return null

  return (
    <>
      {buttons.map((button) => (
        <SocialButton
          key={button.provider}
          {...button}
          {...getButtonPresentation(button.provider, variant)}
          isSubmitting={isSubmitting}
          submittingProvider={submittingProvider}
        />
      ))}

      <div className={cn('flex items-center gap-3 text-sm', isSpecialTheme ? 'text-white/60' : 'text-muted-foreground')}>
        <div className="h-px flex-1 bg-current/30" />
        <span>{separatorLabel}</span>
        <div className="h-px flex-1 bg-current/30" />
      </div>
    </>
  )
}
